import { useState, type ComponentProps } from "react";
import { Button } from "@/components/ui";
import { ContextRing } from "@/components/ui/context-ring";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { contextUsagePercent, formatContextUsage } from "@/lib/context-usage";
import { isTauri } from "@/lib/is-tauri";
import { translate, useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import { InputBarConversationContext } from "./input-bar-conversation-context";
import {
  InputBarModelPill,
  type InputBarFocusedAgent as FocusedAgentInfo,
  type InputBarProvider,
} from "./input-bar-model-pill";
import type {
  DictationMicControl,
  InputBarConversationController,
  InputBarWorkerControls,
} from "./input-bar-types";
import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";

const CHIP_SIZE = "h-7 w-7";

function chipIconSize(compact: boolean): string {
  return compact ? "h-4 w-4" : "h-3.5 w-3.5";
}

interface InputBarActionsProps {
  value: string;
  disabled?: boolean;
  isStreaming: boolean;
  canSubmit: boolean;
  textOnly?: boolean;
  compact: boolean;
  focusedAgent: FocusedAgentInfo | null;
  conversationController?: InputBarConversationController;
  workerControls?: InputBarWorkerControls;
  providers?: InputBarProvider[];
  onUpdateAgentModel?: CursorInputContextValue["onUpdateAgentModel"];
  onUpdateAgentEffort?: CursorInputContextValue["onUpdateAgentEffort"];
  onStop?: () => void;
  companionDisconnected?: boolean;
  onFixCompanion?: () => void;
}

function workerControlLabel(
  control: NonNullable<InputBarWorkerControls["control"]>,
): string {
  const m = inputBarMessages;
  if (control.action === "resume") {
    return translate(m, control.pending ? "resuming" : "resume");
  }
  return translate(m, control.pending ? "stopping" : "stop");
}

/** A composer's dictation mic, pressed while its own recording runs.
 *  `default` is the chip at the leading edge of a conversation-frame or
 *  worker bar; `standalone` is the ghost icon button in the standalone bar's
 *  action row and the tray composer. */
export function InputBarMicButton({
  mic,
  compact = false,
  density = "default",
  "data-testid": testId = "input-bar-mic",
}: {
  mic: DictationMicControl;
  compact?: boolean;
  density?: "default" | "standalone";
  "data-testid"?: string;
}) {
  const t = useT(inputBarMessages);
  const label = mic.recording ? t("stopDictation") : t("dictate");
  if (density === "standalone") {
    return (
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        onClick={mic.onClick}
        disabled={mic.disabled}
        aria-label={label}
        aria-pressed={mic.recording}
        data-testid={testId}
      >
        <Icons.Mic className="h-4 w-4" />
      </Button>
    );
  }
  return (
    <button
      type="button"
      onClick={mic.onClick}
      disabled={mic.disabled}
      aria-label={label}
      aria-pressed={mic.recording}
      data-testid={testId}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full transition-colors",
        CHIP_SIZE,
        mic.recording
          ? "text-accent"
          : "text-muted hover:bg-glass-stroke-subtle hover:text-secondary",
        mic.disabled && "cursor-not-allowed opacity-40",
      )}
    >
      <Icons.Mic className={chipIconSize(compact)} />
    </button>
  );
}

/** The input bar's round send chip; `active` while there is something to
 *  send. Shared with the selection "Ask Denker" composer. */
export function InputBarSendButton({
  active,
  className,
  children,
  ...props
}: ComponentProps<"button"> & { active: boolean }) {
  return (
    <button
      {...props}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full transition-colors",
        CHIP_SIZE,
        active
          ? "text-primary hover:text-secondary"
          : "text-muted hover:text-primary",
        className,
      )}
    >
      {children}
    </button>
  );
}

/** Right-edge action cluster of the input bar.
 *
 *  Layout: [queued?][chip slot][stop|resume?][send].
 *  Chip slot is exclusive — at most one of:
 *    1. wrench (companion disconnected)
 *    2. model + context controls (chat mode, including drafts)
 *    3. read-only worker model label and context ring (task worker composer)
 *  Send button is always rendered except while Stop/Resume shows with empty input. */
export function InputBarActions({
  disabled = false,
  isStreaming,
  canSubmit,
  compact,
  focusedAgent,
  conversationController,
  workerControls,
  providers,
  onUpdateAgentModel,
  onUpdateAgentEffort,
  onStop,
  companionDisconnected,
  onFixCompanion,
}: InputBarActionsProps) {
  const t = useT(inputBarMessages);
  const chipSize = CHIP_SIZE;
  const iconSize = chipIconSize(compact);
  const showWrench = companionDisconnected && onFixCompanion;
  const showModelPillSlot = !showWrench && !!focusedAgent;
  const showWorkerStatus = !showWrench;
  const workerModelLabel = showWorkerStatus ? workerControls?.modelLabel : null;
  const workerContextUsage = showWorkerStatus
    ? workerControls?.contextUsage
    : null;
  const workerContextPercent = workerContextUsage
    ? contextUsagePercent(workerContextUsage)
    : null;
  const workerContextMenuAgent = showWorkerStatus
    ? (workerControls?.contextMenuAgent ?? null)
    : null;
  const [workerContextOpen, setWorkerContextOpen] = useState(false);
  const showNewConversation =
    disabled &&
    !!conversationController &&
    conversationController.canStartNewConversation !== false;
  const workerControl = workerControls?.control ?? null;
  const showStop = (isStreaming || !!workerControl) && !showNewConversation;

  return (
    <>
      {!!workerControls?.queuedCount && (
        <span
          role="status"
          data-testid="input-bar-queued"
          className="shrink-0 whitespace-nowrap px-1 text-appkit-mini text-muted"
        >
          {t("queued", { count: workerControls.queuedCount })}
        </span>
      )}
      {showWrench && (
        <button
          type="button"
          onClick={onFixCompanion}
          aria-label={t("fixCompanion")}
          title={t("companionDisconnected")}
          data-testid="input-bar-fix-companion"
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full text-warning transition-colors",
            chipSize,
          )}
        >
          <Icons.Wrench className={iconSize} />
        </button>
      )}
      {showModelPillSlot && (
        <div data-testid="input-bar-model-pill-wrapper" className="shrink-0">
          <InputBarModelPill
            focusedAgent={focusedAgent}
            conversationController={conversationController}
            providers={
              providers ?? [
                {
                  type: "claude_cli",
                  runtimeId: "claude_cli",
                  loggedIn: false,
                },
              ]
            }
            onUpdateModel={onUpdateAgentModel ?? (async () => {})}
            onUpdateEffort={onUpdateAgentEffort ?? (async () => {})}
          />
        </div>
      )}
      {workerModelLabel && (
        <span
          title={workerModelLabel}
          data-testid="input-bar-worker-model"
          className="flex h-7 shrink-0 items-center px-2 text-appkit-small text-secondary"
        >
          <span
            className={cn(
              "max-w-[110px] truncate whitespace-nowrap",
              compact && "max-w-[100px]",
            )}
          >
            {workerModelLabel}
          </span>
        </span>
      )}
      {workerContextMenuAgent ? (
        <InputBarConversationContext
          focusedAgent={workerContextMenuAgent}
          workerUsage={workerContextUsage ?? null}
          open={workerContextOpen}
          onOpenChange={setWorkerContextOpen}
        />
      ) : (
        workerContextUsage &&
        workerContextPercent !== null && (
          <span
            title={formatContextUsage(
              workerContextUsage.used,
              workerContextUsage.max,
            )}
            data-testid="worker-composer-context-ring"
            className="flex h-7 w-7 shrink-0 items-center justify-center"
          >
            <ContextRing percent={workerContextPercent} size={16} />
          </span>
        )
      )}
      {showStop && (
        <button
          type="button"
          onClick={() => (workerControl ? workerControl.onClick() : onStop?.())}
          disabled={workerControl?.disabled}
          aria-label={
            workerControl
              ? workerControlLabel(workerControl)
              : isTauri()
                ? t("stopGenerating")
                : t("stop")
          }
          title={workerControl ? workerControlLabel(workerControl) : undefined}
          data-testid={
            workerControl?.action === "resume"
              ? "input-bar-resume"
              : "input-bar-stop"
          }
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full text-secondary transition-colors hover:text-primary disabled:cursor-not-allowed disabled:opacity-40",
            chipSize,
          )}
        >
          {workerControl?.action === "resume" ? (
            <Icons.Play className={cn(iconSize, "fill-current")} />
          ) : (
            <Icons.Square className={cn(iconSize, "fill-current")} />
          )}
        </button>
      )}
      {(!showStop || canSubmit) && (
        <InputBarSendButton
          type={canSubmit ? "submit" : "button"}
          active={canSubmit}
          disabled={!canSubmit && !showNewConversation}
          onClick={
            showNewConversation
              ? conversationController.onNewConversation
              : undefined
          }
          aria-label={showNewConversation ? t("newChat") : t("send")}
          title={showNewConversation ? t("newChat") : undefined}
          data-testid={
            showNewConversation ? "input-bar-new-chat" : "input-bar-send"
          }
        >
          {showNewConversation ? (
            <Icons.Plus className={iconSize} />
          ) : (
            <Icons.ArrowUp className={iconSize} />
          )}
        </InputBarSendButton>
      )}
    </>
  );
}
